import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from "react";
import { usePublishImpersonationTarget } from "@/contexts/ImpersonationTargetContext";

export interface ProjectAdminUser {
  id: string;
  nombre: string;
  email: string;
  rol_nombre: string;
  /** Rol con el que entra al Portal Tickets (menús de la vista). */
  rol_id?: number | null;
  persona_id?: number | null;
}

interface Ctx {
  impersonatedUser: ProjectAdminUser | null;
  setImpersonatedUser: (u: ProjectAdminUser | null) => void;
  clearImpersonation: () => void;
  isImpersonating: boolean;
}

const PortalTicketsImpersonationContext = createContext<Ctx | null>(null);
const STORAGE_KEY = "sozu-tickets-impersonated-project-admin";

export function PortalTicketsImpersonationProvider({ children }: { children: ReactNode }) {
  const [impersonatedUser, setImpersonatedUserState] = useState<ProjectAdminUser | null>(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });

  useEffect(() => {
    if (impersonatedUser) localStorage.setItem(STORAGE_KEY, JSON.stringify(impersonatedUser));
    else localStorage.removeItem(STORAGE_KEY);
  }, [impersonatedUser]);

  // Publica al núcleo global: menús/rol efectivos y solo lectura de toda la app.
  usePublishImpersonationTarget(
    impersonatedUser
      ? {
          email: impersonatedUser.email,
          personaId: impersonatedUser.persona_id ?? null,
          nombre: impersonatedUser.nombre,
          rolId: impersonatedUser.rol_id ?? null,
          rolNombre: impersonatedUser.rol_nombre,
          // `id` es su auth_user_id: las lecturas del servidor responden como la persona.
          authUserId: impersonatedUser.id,
        }
      : null,
  );

  const setImpersonatedUser = useCallback((u: ProjectAdminUser | null) => setImpersonatedUserState(u), []);
  const clearImpersonation = useCallback(() => setImpersonatedUserState(null), []);

  return (
    <PortalTicketsImpersonationContext.Provider
      value={{
        impersonatedUser,
        setImpersonatedUser,
        clearImpersonation,
        isImpersonating: !!impersonatedUser,
      }}
    >
      {children}
    </PortalTicketsImpersonationContext.Provider>
  );
}

export function usePortalTicketsImpersonation() {
  const ctx = useContext(PortalTicketsImpersonationContext);
  if (!ctx)
    throw new Error(
      "usePortalTicketsImpersonation must be used within PortalTicketsImpersonationProvider",
    );
  return ctx;
}